import Avatar from '@ui/Avatar';
import type { ReviewDocument } from 'models/review';
import type { PublicUserDocument } from 'models/user';
import Link from 'next/link';
import { StatusLabel, useOnlineStatus } from '@util/hooks/useOnlineStatus';
import { useChat } from 'context/ChatContext';
import ReviewCard from './cards/ReviewCard';
import CarouselComponent from '@ui/Carousel';
import { useAuth } from 'context/AuthContext';
import { SellerAccolade } from './seller/Accolades';
import { cn } from '@util/index';

export default function ProductMeetTheSeller({
  sellerDoc,
  reviews,
  product_id,
  isLoadingReviews,
}: {
  sellerDoc: PublicUserDocument;
  reviews: ReviewDocument[];
  product_id: string;
  isLoadingReviews?: boolean;
}) {
  const { userDoc } = useAuth();
  const { openChatModalWithRecipientAndProductId } = useChat();

  const { status } = useOnlineStatus(sellerDoc?.uid ?? '');

  const hasVisibleAccolades =
    (sellerDoc.badge_counts?.quick_replies ?? 0) > 0 ||
    (sellerDoc.badge_counts?.fast_shipper ?? 0) > 0 ||
    (sellerDoc.badge_counts?.as_described ?? 0) > 0;

  return (
    <div className="flex w-full flex-col gap-y-16 bg-[#F6F6F6]">
      <h1 className="px-16 text-[3rem] font-semibold text-black sm:px-0">
        Meet The Seller
      </h1>

      <div className="flex flex-col items-start gap-x-8 py-4 sm:flex-row">
        <div className="flex w-full flex-shrink-0 flex-col gap-y-4 rounded-[2rem] bg-white p-[2rem] shadow-sm sm:w-[35rem]">
          <div className="flex items-center gap-x-6">
            <Avatar user={sellerDoc} size="medium" />

            <div className="flex flex-col gap-y-2">
              <Link
                href={`/profile/${sellerDoc?.username_slug}`}
                className="text-[1.6rem] font-bold text-brand-secondary hover:underline"
              >
                {sellerDoc?.username}
              </Link>

              <StatusLabel
                status={status}
                hide_status={sellerDoc.hide_status}
                showLastLogin={userDoc?.roles?.support}
                recipientDocUid={sellerDoc.uid}
              />
            </div>
          </div>

          <div className="mt-2 flex w-full items-center justify-between px-[2rem]">
            <div className="flex flex-col items-center gap-y-2">
              <div className="flex items-center gap-x-2">
                <span className="text-[2.1rem] font-semibold text-black">
                  {sellerDoc.rating?.toFixed(1)}
                </span>

                <StarIcon className="h-[2.2rem] w-auto text-brand-secondary" />
              </div>

              <span className="text-[1.3rem] text-[#444444]">Rating</span>
            </div>

            <div className="flex flex-col items-center gap-y-2">
              <div className="flex items-start gap-x-2">
                <span className="text-[2.1rem] font-semibold text-black">
                  {sellerDoc?.reviews ?? 0}
                </span>
              </div>

              <span className="text-[1.3rem] text-[#444444]">Reviews</span>
            </div>

            <div className="flex flex-col items-center gap-y-2">
              <div className="flex items-start gap-x-2">
                <span className="text-[2.1rem] font-semibold text-black">
                  {sellerDoc?.num_sold ?? 0}
                </span>
              </div>

              <span className="text-[1.3rem] text-[#444444]">Items Sold</span>
            </div>
          </div>

          <div className="mt-4 flex flex-col gap-y-4">
            <button
              type="button"
              className="flex w-full items-center justify-center rounded-[1.5rem] border border-zinc-500 bg-inherit p-4 text-[1.8rem] font-semibold text-zinc-800"
              onClick={() => {
                openChatModalWithRecipientAndProductId({
                  recipientUid: sellerDoc?.uid ?? '',
                  productId: product_id,
                });
              }}
            >
              Message Seller
            </button>

            <Link
              href={`/profile/${sellerDoc?.username_slug ?? ''}`}
              type="button"
              className="flex w-full items-center justify-center rounded-[1.5rem] border border-zinc-500 bg-inherit p-4 text-[1.8rem] font-semibold text-zinc-800"
            >
              View Store
            </Link>
          </div>
        </div>

        <div className="flex w-full flex-col gap-y-[2.4rem] pl-[2rem]">
          {hasVisibleAccolades && (
            <div className="mt-12 flex flex-wrap gap-x-20 gap-y-8 sm:mt-0 sm:flex-row sm:justify-between">
              <SellerAccolade
                accolade="quick_responder"
                count={sellerDoc.badge_counts?.quick_replies ?? 0}
                title="Quick Responder"
                description="Responds within 3 hours."
              />

              <SellerAccolade
                accolade="fast_shipper"
                count={sellerDoc.badge_counts?.fast_shipper ?? 0}
                title="Fast Shipper"
                description="Ships within 3 business days."
              />

              <SellerAccolade
                accolade="accurate_describer"
                count={sellerDoc.badge_counts?.as_described ?? 0}
                title="Accurate Describer"
                description="Items match their descriptions."
              />
            </div>
          )}

          {isLoadingReviews ? (
            <>
              <h1
                className={cn(
                  'text-[2.4rem] font-semibold text-black sm:hidden',
                  !hasVisibleAccolades && 'mt-12'
                )}
              >
                Reviews
              </h1>

              <div
                className={cn(
                  'no-scrollbar mx-auto -mt-4 hidden w-[100rem] sm:block'
                )}
              >
                <div className="flex gap-x-8">
                  {[
                    'skeleton-desktop-1',
                    'skeleton-desktop-2',
                    'skeleton-desktop-3',
                  ].map((key) => (
                    <div
                      key={key}
                      className="h-[19.5rem] w-[30rem] animate-pulse rounded-[2rem] bg-white"
                    >
                      <div className="flex h-full flex-col gap-[1.6rem] rounded-[2rem] bg-white p-[1.6rem] shadow-sm">
                        <div className="h-[3rem] w-[12rem] animate-pulse rounded bg-gray-200" />
                        <div className="flex-1 space-y-2">
                          <div className="h-4 w-full animate-pulse rounded bg-gray-200" />
                          <div className="h-4 w-full animate-pulse rounded bg-gray-200" />
                          <div className="h-4 w-3/4 animate-pulse rounded bg-gray-200" />
                        </div>
                        <div className="flex items-center gap-[0.8rem]">
                          <div className="h-[3.2rem] w-[3.2rem] animate-pulse rounded-full bg-gray-200" />
                          <div className="h-4 w-[10rem] animate-pulse rounded bg-gray-200" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div
                className={cn(
                  'flex overflow-x-auto overflow-y-hidden sm:hidden'
                )}
              >
                <div className="my-8 flex items-center gap-x-8 pr-32">
                  {[
                    'skeleton-mobile-1',
                    'skeleton-mobile-2',
                    'skeleton-mobile-3',
                  ].map((key) => (
                    <div
                      key={key}
                      className="h-[19.5rem] w-[30rem] flex-shrink-0 animate-pulse rounded-[2rem] bg-white"
                    >
                      <div className="flex h-full flex-col gap-[1.6rem] rounded-[2rem] bg-white p-[1.6rem] shadow-sm">
                        <div className="h-[3rem] w-[12rem] animate-pulse rounded bg-gray-200" />
                        <div className="flex-1 space-y-2">
                          <div className="h-4 w-full animate-pulse rounded bg-gray-200" />
                          <div className="h-4 w-full animate-pulse rounded bg-gray-200" />
                          <div className="h-4 w-3/4 animate-pulse rounded bg-gray-200" />
                        </div>
                        <div className="flex items-center gap-[0.8rem]">
                          <div className="h-[3.2rem] w-[3.2rem] animate-pulse rounded-full bg-gray-200" />
                          <div className="h-4 w-[10rem] animate-pulse rounded bg-gray-200" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <>
              {!!reviews.length && (
                <>
                  <h1
                    className={cn(
                      'text-[2.4rem] font-semibold text-black sm:hidden',
                      !hasVisibleAccolades && 'mt-12'
                    )}
                  >
                    Reviews
                  </h1>

                  <div
                    className={cn(
                      'no-scrollbar mx-auto -mt-4 hidden w-[100rem] sm:block'
                    )}
                  >
                    <CarouselComponent
                      items={reviews.map((review) => (
                        <ReviewCard
                          key={review.id}
                          review={review}
                          className="!w-[30rem]"
                        />
                      ))}
                      incrementBy={1}
                      basis="basis-1/3.5"
                    />
                  </div>

                  <div
                    className={cn(
                      'flex overflow-x-auto overflow-y-hidden sm:hidden'
                    )}
                  >
                    <div className="my-8 flex items-center gap-x-8 pr-32">
                      {reviews.map((review) => (
                        <ReviewCard
                          key={review.id}
                          review={review}
                          widthClassName="!w-[30rem] flex-shrink-0"
                        />
                      ))}
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <Link
                      href={`/profile/${sellerDoc?.username_slug ?? ''}`}
                      type="button"
                      className="mt-4 flex w-fit min-w-[20rem] flex-shrink-0 items-center justify-center rounded-[1.5rem] border border-zinc-800 bg-inherit px-8 py-4 text-[1.8rem] font-semibold text-zinc-800"
                    >
                      Show all reviews
                    </Link>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const StarIcon = ({
  className,
  isActive = true,
}: {
  className?: string;
  isActive?: boolean;
}) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <title>Star Icon</title>
    <path
      d="M10.0013 14.8083L15.1513 17.9166L13.7846 12.0583L18.3346 8.11665L12.343 7.60831L10.0013 2.08331L7.65964 7.60831L1.66797 8.11665L6.21797 12.0583L4.8513 17.9166L10.0013 14.8083Z"
      fill={isActive ? '#C41719' : '#D4D4D8'}
    />
  </svg>
);
